import { useEffect, useState } from "react";

import { businessDate } from "src/lib/dates";

/**
 * useBusinessDate returns the business calendar date and re-renders when it changes.
 *
 * Engagement status is derived from dates on the server, so it goes stale with no
 * write behind it: a console left open across midnight would show yesterday's answer
 * indefinitely, because nothing invalidates a query when no mutation happened and
 * refetchOnWindowFocus is off.
 *
 * Feeding this into a query key makes the boundary invalidate the cache. The list
 * screen used to get this for free — its start-date cutoff was part of the filter,
 * so crossing midnight changed the key — and removing that cutoff removed the
 * mechanism with it.
 *
 * A once-a-minute check rather than a timer aimed at the next midnight: computing
 * that instant means doing zone arithmetic that a DST change or a zone-rule update
 * can get wrong, and the failure would be silent and once-a-day. The cost of
 * checking is one formatted date a minute, and React's own bail-out means an
 * unchanged answer re-renders nothing.
 */
export function useBusinessDate(): string {
  const [date, setDate] = useState(businessDate);

  useEffect(() => {
    const id = setInterval(() => {
      // No same-value guard: React compares with Object.is and bails out when the
      // string is unchanged, so all but one tick a day already costs nothing. An
      // explicit guard here read as though it were load-bearing, and removing it
      // changed no test — which is how it was found.
      setDate(businessDate());
    }, 60_000);
    return () => clearInterval(id);
  }, []);

  return date;
}
